<template>
  <section>
    <b-card
      class="mb-0"
      no-body
    >
      <b-tabs nav-class="mb-0">
        <b-tab
          id="campaigns-tab"
          :active="wrapper.tab === 'campaigns'"
          @click="onTabChange('campaigns')"
        >
          <template v-slot:title>
            <span class="d-none d-sm-inline-block">
              <GjIcon name="Box" />
              {{ $tk('ClientPortal.MasterTable.Campaigns') }}
            </span>
            <template v-if="!loading">
              <b-badge
                v-if="wrapper.config.filter.campaignIds.length > 0"
                class="ml-50 py-0"
                variant="primary"
                pill
              >
                {{ wrapper.config.filter.campaignIds.length }} {{ $tk('ClientPortal.MasterTable.Selected') }}
                <b-button
                  size="sm"
                  variant="link"
                  class="text-white p-0"
                  @click="selectAllCampaigns(false)"
                >
                  <GjIcon
                    class="mr-0"
                    name="Close"
                    @click="selectAllCampaigns(false)"
                  />
                </b-button>
              </b-badge>
            </template>
          </template>
          <master-table-header-skeleton v-if="loading" />
          <master-table-header
            v-else
            :wrapper="wrapper"
            :breakdown-text="breakdownText"
            :loading-statistics="loadingStatistics"
            :is-tab-campaign="true"
            @openPreset="openPresetModal"
            @breakdown="setBreakdown"
            @archive="archiveCampaigns"
            @delete="deleteCampaigns"
            @changeStatus="changeCampaignStatus"
          />
          <!--Campaign master table-->
          <div
            class="table table-max-height overflow-auto"
            @mousemove="resizeCampaign"
          >
            <div
              v-if="!loading"
              class="table-header"
            >
              <div class="table-row">
                <div class="table-cell table-cell-sticky">
                  <div class="table-cell_text">
                    <div class="form-check mt-0 mb-0 pl-0">
                      <b-form-checkbox
                        v-model="stats.campaignTabStatistics.selectedAll"
                        @change="selectAllCampaigns($event)"
                      />
                    </div>
                  </div>
                </div>
                <div
                  ref="campaign"
                  class="table-cell table-cell-title table-cell-sticky"
                  :class="getColumnSortedClass('name')"
                >
                  <div
                    class="table-cell_text"
                    @click="setSortDirection('name')"
                  >
                    <div>
                      {{ $tk('ClientPortal.MasterTable.Campaigns') }}
                    </div>
                    <span
                      v-if="wrapper.config.filter.sortColumn == 'name'"
                      class="ml-auto"
                    >
                      <GjIcon
                        :key="getSortOrder('name')"
                        :name="getSortOrder('name')"
                      />
                    </span>
                  </div>
                </div>
                <div
                  v-for="(presetColumnKey, index) in wrapper.config.preset.columns"
                  :key="index"
                  class="table-cell"
                  :class="addCustomClass(presetColumnKey)"
                  @click="setSortDirection(presetColumnKey)"
                >
                  <div class="table-cell_text">
                    <div>
                      {{ wrapper.columns[presetColumnKey].name }}
                    </div>

                    <span v-if="wrapper.config.filter.sortColumn == presetColumnKey">
                      <GjIcon
                        :key="getSortOrder(presetColumnKey)"
                        :name="getSortOrder(presetColumnKey)"
                      />
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div class="table-body">
              <master-table-skeleton v-if="loading" />
              <div
                v-else-if="stats.campaignTabStatistics.entities.length == 0"
                class="empty-table d-flex justify-content-center align-items-center"
              >
                <template v-if="loadingStatistics">
                  <loader
                    v-if="loadingStatistics"
                    :size="20"
                    :show="true"
                  />
                </template>
                <template v-else>
                  <div class="text-center">
                    <h3>
                      {{ $tk('ClientPortal.MasterTable.NoCampaignsYet') }}
                    </h3>
                    <b-button
                      v-if="accountAccessManager.canManageCampaigns()"
                      variant="primary"
                      size="sm"
                      :to="'/' +$route.params.accountId + '/campaign/create'"
                      class="btn-icon-text"
                    >
                      {{ $tk('ClientPortal.MasterTable.Create') }}
                    </b-button>
                  </div>
                </template>
              </div>
              <master-table-row
                v-for="campaign in stats.campaignTabStatistics.entities"
                :key="campaign.entityType + '-sel-' + campaign.id"
                :entity="campaign"
                :preset="wrapper.config.preset"
                :columns="wrapper.columns"
                :statuses="wrapper.statuses"
                :level="0"
                :selectable="true"
                :titlewidth="titleWidthCampaign"
                :tab="'Campaign'"
                @copy="copyEntity"
                @select="onEntitySelect"
                @click="onEntityClick"
                @edit="editEntity"
              />
            </div>
            <div class="table-footer">
              <div class="table-row">
                <master-table-row
                  v-if="!loading"
                  :key="'campaigns-summary'"
                  :entity="campaignsSummary"
                  :preset="wrapper.config.preset"
                  :statuses="wrapper.statuses"
                  :columns="wrapper.columns"
                  :level="0"
                  :selectable="false"
                  :titlewidth="titleWidthCampaign"
                />
              </div>
            </div>
          </div>
          <!--PAGINATION-->
          <div
            v-if="!loading"
            class="d-flex border-top p-1"
          >
            <b-pagination
              :value="campaignCurrentPage"
              :total-rows="stats.campaignTabStatistics.totalEntities"
              :per-page="wrapper.adStatisticsTableSize"
              size="sm"
              class="my-0 mr-1"
              hide-goto-end-buttons
              @input="value => changePage(value)"
            >
              <template
                #prev-text
                :disabled="wrapper.config.filter.campaignPage === 0"
              >
                <GjIcon
                  class="mr-25"
                  name="ArrowLeft"
                />
              </template>
              <template
                #next-text
                :disabled="(wrapper.config.filter.campaignPage + 1) * wrapper.adStatisticsTableSize >= stats.campaignTabStatistics.totalEntities"
              >
                <GjIcon
                  class="ml-25"
                  name="ArrowRight"
                />
              </template>
            </b-pagination>
            <span>
              {{ $tk('ClientPortal.Pagination.Viewing') }} {{ (wrapper.config.filter.campaignPage * wrapper.adStatisticsTableSize) + 1 }}
              {{ $tk('ClientPortal.Pagination.To') }} {{ Math.min((wrapper.config.filter.campaignPage + 1) * wrapper.adStatisticsTableSize, stats.campaignTabStatistics.totalEntities) }}
              {{ $tk('ClientPortal.Pagination.Of') }}  {{ stats.campaignTabStatistics.totalEntities }}
              {{ $tk('ClientPortal.Pagination.Items') }}
            </span>
          </div>
        </b-tab>
        <!--AdGroups Master Table-->
        <b-tab
          id="adgroups-tab"
          :active="wrapper.tab === 'adgroups'"
          @click="onTabChange('adgroups')"
        >
          <template v-slot:title>
            <span class="d-none d-sm-inline-block mr-25">
              <GjIcon name="Folder" />
              {{ $tk('ClientPortal.MasterTable.AdGroups') }}
            </span>
            <template v-if="!loading">
              <b-badge
                v-if="wrapper.config.filter.adGroupIds.length > 0"
                class="mx-50 py-0"
                variant="primary"
                pill
              >
                {{ wrapper.config.filter.adGroupIds.length }} {{ $tk('ClientPortal.MasterTable.Selected') }}
                <b-button
                  size="sm"
                  variant="link"
                  class="text-white p-0"
                  @click="selectAllAdGroups(false)"
                >
                  <GjIcon
                    class="mr-0"
                    name="Close"
                  />
                </b-button>
              </b-badge>
              <span v-else>
                <span v-if="wrapper.config.filter.campaignIds.length == 1">
                  {{ $tk('ClientPortal.MasterTable.For') }} {{ wrapper.config.filter.campaignIds.length }} {{ $tk('ClientPortal.MasterTable.Campaign') }}
                </span>
                <span v-else-if="wrapper.config.filter.campaignIds.length > 1">
                  {{ $tk('ClientPortal.MasterTable.For') }} {{ wrapper.config.filter.campaignIds.length }} {{ $tk('ClientPortal.MasterTable.Campaigns') }}
                </span>
              </span>
            </template>
          </template>
          <master-table-header-skeleton v-if="loading" />
          <master-table-header
            v-else
            :wrapper="wrapper"
            :breakdown-text="breakdownText"
            :loading-statistics="loadingStatistics"
            :is-tab-ad-group="true"
            @openPreset="openPresetModal"
            @breakdown="setBreakdown"
            @archive="archiveAdGroups"
            @delete="deleteAdGroups"
            @changeStatus="changeAdGroupStatus"
          />
          <div
            class="table table-max-height overflow-auto"
            @mousemove="resizeAdgroup"
          >
            <div
              v-if="!loading"
              class="table-header"
            >
              <div class="table-row">
                <div class="table-cell table-cell-sticky">
                  <div class="table-cell_text">
                    <div class="form-check mt-0 mb-0 pl-0">
                      <b-form-checkbox
                        v-model="stats.adGroupTabStatistics.selectedAll"
                        @change="selectAllAdGroups($event)"
                      />
                    </div>
                  </div>
                </div>
                <div
                  ref="adgroup"
                  class="table-cell table-cell-title table-cell-sticky"
                  :class="getColumnSortedClass('name')"
                >
                  <div
                    class="table-cell_text"
                    @click="setSortDirection('name')"
                  >
                    <div>
                      {{ $tk('ClientPortal.MasterTable.AdGroup') }}
                    </div>
                    <span
                      v-if="wrapper.config.filter.sortColumn == 'name'"
                      class="ml-auto"
                    >
                      <GjIcon
                        :key="getSortOrder('name')"
                        :name="getSortOrder('name')"
                      />
                    </span>
                  </div>
                </div>
                <div
                  v-for="(presetColumnKey, index) in wrapper.config.preset.columns"
                  :key="index"
                  class="table-cell"
                  :class="addCustomClass(presetColumnKey)"
                  @click="setSortDirection(presetColumnKey)"
                >
                  <div class="table-cell_text">
                    <div>
                      {{ wrapper.columns[presetColumnKey].name }}
                    </div>

                    <span v-if="wrapper.config.filter.sortColumn == presetColumnKey">
                      <GjIcon
                        :key="getSortOrder(presetColumnKey)"
                        :name="getSortOrder(presetColumnKey)"
                      />
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div class="table-body">
              <master-table-skeleton v-if="loading" />
              <div
                v-else-if="stats.adGroupTabStatistics.entities.length == 0"
                class="empty-table d-flex justify-content-center align-items-center"
              >
                <template v-if="loadingStatistics">
                  <loader
                    v-if="loadingStatistics"
                    :size="20"
                    :show="true"
                  />
                </template>
                <template v-else>
                  <div class="text-center">
                    <h3>
                      {{ $tk('ClientPortal.MasterTable.NoAdGroupsYet') }}
                    </h3>
                    <b-button
                      v-if="accountAccessManager.canManageAdGroups()"
                      variant="primary"
                      size="sm"
                      :to="createAdGroupUrl"
                      class="btn-icon-text"
                    >
                      {{ $tk('ClientPortal.MasterTable.Create') }}
                    </b-button>
                  </div>
                </template>
              </div>
              <master-table-row
                v-for="adGroup in stats.adGroupTabStatistics.entities"
                :key="adGroup.entityType + '-sel-' + adGroup.id"
                :entity="adGroup"
                :preset="wrapper.config.preset"
                :columns="wrapper.columns"
                :statuses="wrapper.statuses"
                :level="0"
                :selectable="true"
                :titlewidth="titleWidthAdgroup"
                :tab="'AdGroups'"
                @copy="copyEntity"
                @select="onEntitySelect"
                @click="onEntityClick"
                @edit="editEntity"
              />
            </div>
            <div class="table-footer">
              <div class="table-row">
                <master-table-row
                  v-if="!loading"
                  :key="'adgroups-summary'"
                  :entity="adGroupsSummary"
                  :preset="wrapper.config.preset"
                  :statuses="wrapper.statuses"
                  :columns="wrapper.columns"
                  :level="0"
                  :selectable="false"
                  :titlewidth="titleWidthAdgroup"
                />
              </div>
            </div>
          </div>
          <!--PAGINATION-->
          <div
            v-if="!loading"
            class="d-flex border-top p-1"
          >
            <b-pagination
              :value="adGroupCurrentPage"
              :total-rows="stats.adGroupTabStatistics.totalEntities"
              :per-page="wrapper.adStatisticsTableSize"
              size="sm"
              class="my-0 mr-1"
              hide-goto-end-buttons
              @input="value => changePage(value)"
            >
              <template
                #prev-text
                :disabled="wrapper.config.filter.adGroupPage === 0"
              >
                <GjIcon
                  class="mr-25"
                  name="ArrowLeft"
                />
              </template>
              <template
                #next-text
                :disabled="(wrapper.config.filter.adGroupPage + 1) * wrapper.adStatisticsTableSize >= stats.adGroupTabStatistics.totalEntities"
              >
                <GjIcon
                  class="ml-25"
                  name="ArrowRight"
                />
              </template>
            </b-pagination>
            <span>
              {{ $tk('ClientPortal.Pagination.Viewing') }}  {{ (wrapper.config.filter.adGroupPage * wrapper.adStatisticsTableSize) + 1 }}
              {{ $tk('ClientPortal.Pagination.To') }}  {{ Math.min((wrapper.config.filter.adGroupPage + 1) * wrapper.adStatisticsTableSize, stats.adGroupTabStatistics.totalEntities) }}
              {{ $tk('ClientPortal.Pagination.Of') }}  {{ stats.adGroupTabStatistics.totalEntities }} {{ $tk('ClientPortal.Pagination.Items') }}
            </span>
          </div>
        </b-tab>
        <!--Ads Master Table-->
        <b-tab
          id="ads-tab"
          :active="wrapper.tab === 'ads'"
          @click="onTabChange('ads')"
        >
          <template v-slot:title>
            <span class="d-none d-sm-inline-block mr-25">
              <GjIcon name="Desktop" />
              {{ $tk('ClientPortal.MasterTable.Ads') }}
            </span>
            <template v-if="!loading">
              <b-badge
                v-if="wrapper.config.filter.adIds.length > 0"
                class="mx-50 py-0"
                variant="primary"
                pill
              >
                {{ wrapper.config.filter.adIds.length }} {{ $tk('ClientPortal.MasterTable.Selected') }}
                <b-button
                  size="sm"
                  variant="link"
                  class="text-white p-0"
                  @click="selectAllAds(false)"
                >
                  <GjIcon
                    class="mr-0"
                    name="Close"
                  />
                </b-button>
              </b-badge>
              <span v-if="wrapper.config.filter.adGroupIds.length > 0">
                {{ $tk('ClientPortal.MasterTable.For') }} {{ wrapper.config.filter.adGroupIds.length }} {{ $tk('ClientPortal.MasterTable.AdGroups') }}
              </span>
              <span v-else-if="wrapper.config.filter.campaignIds.length > 0">

                {{ $tk('ClientPortal.MasterTable.For') }} {{ wrapper.config.filter.campaignIds.length }} {{ $tk('ClientPortal.MasterTable.Campaigns') }}
              </span>
            </template>
          </template>
          <master-table-header-skeleton v-if="loading" />
          <master-table-header
            v-else
            :wrapper="wrapper"
            :breakdown-text="breakdownText"
            :loading-statistics="loadingStatistics"
            :is-tab-ad="true"
            @openPreset="openPresetModal"
            @breakdown="setBreakdown"
            @archive="archiveAds"
            @delete="deleteAds"
            @changeStatus="changeAdStatus"
          />
          <div
            class="table table-max-height overflow-auto"
            @mousemove="resizeAd"
          >
            <div
              v-if="!loading"
              class="table-header"
            >
              <div class="table-row">
                <div class="table-cell table-cell-sticky">
                  <div class="table-cell_text">
                    <div class="form-check mt-0 mb-0 pl-0">
                      <b-form-checkbox
                        v-model="stats.adTabStatistics.selectedAll"
                        @change="selectAllAds($event)"
                      />
                    </div>
                  </div>
                </div>
                <div
                  ref="ad"
                  class="table-cell table-cell-title table-cell-sticky"
                  :class="getColumnSortedClass('name')"
                >
                  <div
                    class="table-cell_text"
                    @click="setSortDirection('name')"
                  >
                    <div>
                      {{ $tk('ClientPortal.MasterTable.Ads') }}
                    </div>
                    <span
                      v-if="wrapper.config.filter.sortColumn == 'name'"
                      class="ml-auto"
                    >
                      <GjIcon
                        :key="getSortOrder('name')"
                        :name="getSortOrder('name')"
                      />
                    </span>
                  </div>
                </div>
                <div
                  v-for="(presetColumnKey, index) in wrapper.config.preset.columns"
                  :key="index"
                  class="table-cell"
                  :class="addCustomClass(presetColumnKey)"
                  @click="setSortDirection(presetColumnKey)"
                >
                  <div class="table-cell_text">
                    <div>
                      {{ wrapper.columns[presetColumnKey].name }}
                    </div>

                    <span v-if="wrapper.config.filter.sortColumn == presetColumnKey">
                      <GjIcon
                        :key="getSortOrder(presetColumnKey)"
                        :name="getSortOrder(presetColumnKey)"
                      />
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div class="table-body">
              <master-table-skeleton v-if="loading" />
              <div
                v-else-if="stats.adTabStatistics.entities.length == 0"
                class="empty-table d-flex justify-content-center align-items-center"
              >
                <template v-if="loadingStatistics">
                  <loader
                    v-if="loadingStatistics"
                    :size="20"
                    :show="true"
                  />
                </template>
                <template v-else>
                  <div class="text-center">
                    <h3>
                      {{ $tk('ClientPortal.MasterTable.NoAdsYet') }}
                    </h3>
                    <b-button
                      v-if="accountAccessManager.canManageAds()"
                      variant="primary"
                      size="sm"
                      :to="createAdUrl"
                      class="btn-icon-text"
                    >
                      {{ $tk('ClientPortal.MasterTable.Create') }}
                    </b-button>
                  </div>
                </template>
              </div>
              <master-table-row
                v-for="ad in stats.adTabStatistics.entities"
                :key="ad.entityType + '-sel-' + ad.id"
                :entity="ad"
                :preset="wrapper.config.preset"
                :columns="wrapper.columns"
                :statuses="wrapper.statuses"
                :level="0"
                :selectable="true"
                :titlewidth="titleWidthAd"
                :tab="'Ads'"
                @copy="copyEntity"
                @select="onEntitySelect"
                @click="onEntityClick"
                @edit="editEntity"
                @heatmap="openHeatmapModal"
              />
            </div>
            <div class="table-footer">
              <div class="table-row">
                <master-table-row
                  v-if="!loading"
                  :key="'ads-summary'"
                  :entity="adsSummary"
                  :preset="wrapper.config.preset"
                  :statuses="wrapper.statuses"
                  :columns="wrapper.columns"
                  :level="0"
                  :selectable="false"
                  :titlewidth="titleWidthAd"
                />
              </div>
            </div>
          </div>
          <!--PAGINATION-->
          <div
            v-if="!loading"
            class="d-flex border-top p-1"
          >
            <b-pagination
              :value="adCurrentPage"
              :total-rows="stats.adTabStatistics.totalEntities"
              :per-page="wrapper.adStatisticsTableSize"
              size="sm"
              class="my-0 mr-1"
              hide-goto-end-buttons
              @input="value => changePage(value)"
            >
              <template
                #prev-text
                :disabled="wrapper.config.filter.adPage === 0"
              >
                <GjIcon
                  class="mr-25"
                  name="ArrowLeft"
                />
              </template>
              <template
                #next-text
                :disabled="(wrapper.config.filter.adPage + 1) * wrapper.adStatisticsTableSize >= stats.adTabStatistics.totalEntities"
              >
                <GjIcon
                  class="ml-25"
                  name="ArrowRight"
                />
              </template>
            </b-pagination>
            <span>
              {{ $tk('ClientPortal.Pagination.Viewing') }} {{ (wrapper.config.filter.adPage * wrapper.adStatisticsTableSize) + 1 }}
              {{ $tk('ClientPortal.Pagination.To') }} {{ Math.min((wrapper.config.filter.adPage + 1) * wrapper.adStatisticsTableSize, stats.adTabStatistics.totalEntities) }}
              {{ $tk('ClientPortal.Pagination.Of') }}  {{ stats.adTabStatistics.totalEntities }}
              {{ $tk('ClientPortal.Pagination.Items') }}
            </span>
          </div>
        </b-tab>
      </b-tabs>
    </b-card>
    <!--Duplicate Campaign Modal-->
    <b-modal
      id="copy-campaign-modal"
      ref="copyCampaignModal"
      :title="$tk('ClientPortal.MasterTable.DuplicateCampaign')"
      centered
    >
      <b-form-group
        :label="$tk('ClientPortal.MasterTable.NumberOfCopies')"
        label-size="xs"
      >
        <b-form-input
          v-model.number="copyCampaignModel.numberOfCopies"
          size="sm"
          type="number"
          min="1"
          max="20"
        />
      </b-form-group>
      <b-form-group
        :label="$tk('ClientPortal.MasterTable.NewName') + ' *'"
        label-size="xs"
      >
        <b-form-input
          id="campaign-name"
          v-model="copyCampaignModel.name"
          type="text"
          size="sm"
        />
        <span class="field-validation-valid text-danger small">{{ copyCampaignModel.error }}</span>
      </b-form-group>
      <b-form-group
        :label="$tk('ClientPortal.MasterTable.Include')"
        label-size="xs"
        class="mb-0"
      >
        <b-form-checkbox
          id="campaign-duplicate-pause-adgroups"
          v-model="copyCampaignModel.pauseNewAdGroups"
        >
          {{ $tk('ClientPortal.MasterTable.PauseNewAdGroups') }}
        </b-form-checkbox>
        <b-form-checkbox
          id="campaign-duplicate-adgroups"
          v-model="copyCampaignModel.includeAdGroups"
        >
          {{ $tk('ClientPortal.MasterTable.AdGroups') }}
        </b-form-checkbox>
        <b-form-checkbox
          id="adgroup-duplicate-pause-adgroups"
          v-model="copyCampaignModel.copyAdGroupsTargeting"
        >
          {{ $tk('ClientPortal.MasterTable.CopyAdGroupsTargeting') }}
        </b-form-checkbox>
        <b-form-checkbox
          id="campaign-duplicate-ads"
          v-model="copyCampaignModel.includeAds"
        >
          {{ $tk('ClientPortal.MasterTable.Ads') }}
        </b-form-checkbox>
      </b-form-group>
      <template v-slot:modal-footer="{ cancel }">
        <b-button
          variant="outline-secondary"
          size="sm"
          @click="cancel()"
        >
          {{ $tk('ClientPortal.MasterTable.Cancel') }}
        </b-button>
        <b-button
          variant="primary"
          size="sm"
          :disabled="duplicating || !isDuplicateAdModalCompleted(1)"
          @click.prevent="saveCopiedCampaign(copyCampaignModel)"
        >
          {{ $tk('ClientPortal.MasterTable.Duplicate') }}
        </b-button>
      </template>
    </b-modal>
    <!--Duplicate AdGroup Modal-->
    <b-modal
      id="copy-adgroup-modal"
      ref="copyAdgroupModal"
      :title="$tk('ClientPortal.MasterTable.DuplicateAdGroupInto')"
      centered
    >
      <span class="field-validation-valid text-danger small">{{ copyAdGroupModel.error }}</span>
      <b-form-group
        :label="$tk('ClientPortal.MasterTable.NumberOfCopies')"
        label-size="xs"
      >
        <b-form-input
          v-model.number="copyAdGroupModel.numberOfCopies"
          size="sm"
          type="number"
          min="1"
          max="20"
        />
      </b-form-group>
      <b-form-group
        :label="$tk('ClientPortal.MasterTable.SelectCampign' + '*')"
        label-size="xs"
      >
        <MultiselectTags
          id="selectCampaign"
          option-label="name"
          :included-tags="copyAdGroupModel.selectedCampaigns"
          :options="campaignList"
          :group="false"
          :is-duplicating="true"
          :placeholder="$tk('ClientPortal.Form.SearchCampaigns')"
          @addOption="addSelectedCampaign"
          @removeOption="removeSelectedCampaign"
        />
      </b-form-group>
      <b-form-group
        :label="$tk('ClientPortal.MasterTable.Include')"
        label-size="xs"
        class="mb-0"
      >
        <b-form-checkbox
          id="adgroup-duplicate-pause-adgroups"
          v-model="copyAdGroupModel.pauseNewAdGroups"
        >
          {{ $tk('ClientPortal.MasterTable.PauseNewAdGroups') }}
        </b-form-checkbox>
        <b-form-checkbox
          id="adgroup-duplicate-copy-ads"
          v-model="copyAdGroupModel.copyAds"
        >
          {{ $tk('ClientPortal.MasterTable.CopyAds') }}
        </b-form-checkbox>
        <b-form-checkbox
          id="adgroup-duplicate-copy-adgroups-targeting"
          v-model="copyAdGroupModel.copyAdGroupsTargetting"
        >
          {{ $tk('ClientPortal.MasterTable.CopyAdGroupsTargeting') }}
        </b-form-checkbox>
      </b-form-group>
      <template v-slot:modal-footer="{ cancel }">
        <b-button
          variant="outline-secondary"
          size="sm"
          @click="cancel()"
        >
          {{ $tk('ClientPortal.MasterTable.Cancel') }}
        </b-button>
        <b-button
          variant="primary"
          size="sm"
          :disabled="duplicating || !isDuplicateAdModalCompleted(2)"
          @click="saveCopiedAdGroup(copyAdGroupModel)"
        >
          {{ $tk('ClientPortal.MasterTable.Duplicate') }}
        </b-button>
      </template>
    </b-modal>
    <!--Duplicate Ad Modal-->
    <b-modal
      id="copy-ad-modal"
      ref="copyAdCopyModal"
      :title="$tk('ClientPortal.MasterTable.DuplicateAdInto')"
      centered
    >
      <b-form-group
        :label="$tk('ClientPortal.MasterTable.NumberOfCopies')"
        label-size="xs"
      >
        <b-form-input
          v-model.number="copyAdModel.numberOfCopies"
          size="sm"
          type="number"
          min="1"
        />
      </b-form-group>
      <b-form-group
        :label="$tk('ClientPortal.MasterTable.SelectAdGroup') + ' *'"
        label-size="xs"
      >
        <MultiselectTags
          id="selectAdGroup"
          option-label="name"
          group-values="adGroups"
          :included-tags="copyAdModel.selectedAdGroups"
          :options="adGroupList"
          :placeholder="$tk('ClientPortal.Form.SearchAdGroups')"
          @includeTag="addSelectedAdGroup"
          @removeTag="removeSelectedAdGroup"
        />
      </b-form-group>
      <b-form-group
        :label="$tk('ClientPortal.MasterTable.Include')"
        label-size="xs"
        class="mb-0"
      >
        <b-form-checkbox
          id="ad-duplicate-pause-ads"
          v-model="copyAdModel.pauseNewAds"
        >
          {{ $tk('ClientPortal.MasterTable.PauseNewAds') }}
        </b-form-checkbox>
      </b-form-group>
      <template v-slot:modal-footer="{ cancel }">
        <b-button
          variant="outline-secondary"
          size="sm"
          @click="cancel()"
        >
          {{ $tk('ClientPortal.MasterTable.Cancel') }}
        </b-button>
        <b-button
          variant="primary"
          size="sm"
          :disabled="duplicating || !isDuplicateAdModalCompleted(3)"
          @click="saveCopiedAd(copyAdModel)"
        >
          {{ $tk('ClientPortal.MasterTable.Duplicate') }}
        </b-button>
      </template>
    </b-modal>
    <b-modal
      id="confirmationModal"
      ref="confirmationModal"
      class="fade"
      centered
    >
      <template v-slot:modal-title>
        <span v-if="confirmDialogType.actionType == 11">
          {{ $tk('ClientPortal.MasterTable.ConfirmDialog.AreYouSureYouWantToArchive') }}
        </span>
        <span v-else>
          {{ $tk('ClientPortal.MasterTable.ConfirmDialog.AreYouSureYouWantToDelete') }}
        </span>
      </template>
      <div
        v-if="confirmDialogType.loading"
        class="loading-content my-5"
      >
        <loader
          :size="20"
          :show="true"
        />
      </div>
      <div v-else>
        <span v-if="confirmDialogType.tabType == tabEnum.CAMPAIGNS">
          <span v-if="confirmDialogType.actionType == 11">
            {{ $tk('ClientPortal.MasterTable.ConfirmDialog.AreYouSureYouWantToArchive') }}
          </span>
          <span v-else>
            {{ $tk('ClientPortal.MasterTable.ConfirmDialog.AreYouSureYouWantToDelete') }}
          </span>
          {{ $tk('ClientPortal.MasterTable.ConfirmDialog.SelectedCampaigns') }}
        </span>
        <span v-else-if="confirmDialogType.tabType == tabEnum.AD_GROUPS">
          <span v-if="confirmDialogType.actionType == 11">
            {{ $tk('ClientPortal.MasterTable.ConfirmDialog.AreYouSureYouWantToArchive') }}
          </span>
          <span v-else>
            {{ $tk('ClientPortal.MasterTable.ConfirmDialog.AreYouSureYouWantToDelete') }}
          </span>
          {{ $tk('ClientPortal.MasterTable.ConfirmDialog.SelectedAdGroups') }}
        </span>
        <span v-else>
          <span v-if="confirmDialogType.actionType == 11">
            {{ $tk('ClientPortal.MasterTable.ConfirmDialog.AreYouSureYouWantToArchive') }}
          </span>
          <span v-else>
            {{ $tk('ClientPortal.MasterTable.ConfirmDialog.AreYouSureYouWantToDelete') }}
          </span>
          {{ $tk('ClientPortal.MasterTable.ConfirmDialog.SelectedAds') }}
        </span>
      </div>
      <template v-slot:modal-footer="{ cancel }">
        <b-button
          variant="outline-secondary"
          size="sm"
          @click="cancel()"
        >
          {{ $tk('ClientPortal.MasterTable.Cancel') }}
        </b-button>
        <button
          type="button"
          :disabled="confirmDialogType.loading"
          class="btn btn-sm"
          :class="confirmDialogType.actionType == 12 ? 'btn-danger': 'btn-primary'"
          @click="confirmDialogYes"
        >
          <span v-if="confirmDialogType.actionType == 11">
            {{ $tk('ClientPortal.MasterTable.ConfirmDialog.Archive') }}
          </span>
          <span v-else>
            {{ $tk('ClientPortal.MasterTable.ConfirmDialog.Delete') }}
          </span>
        </button>
      </template>
    </b-modal>
    <!--Preset Modal-->
    <b-modal
      id="preset-modal"
      ref="presetModal"
      size="lg"
      body-class="modal-body--no-padding-preset"
      centered
      :title="$tk('ClientPortal.MasterTable.CustomizeColumnsPreset')"
    >
      <b-table-simple
        v-if="!loading"
        class="mb-0"
        fixed
      >
        <b-tbody>
          <b-tr>
            <b-td class="border-right">
              {{ $tk('ClientPortal.MasterTable.Presets') }}
            </b-td>
            <b-td class="border-right">
              {{ $tk('ClientPortal.MasterTable.AvailableColumns') }}
            </b-td>
            <b-td>
              {{ $tk('ClientPortal.MasterTable.ColumnOrder') }}
            </b-td>
          </b-tr>
          <b-tr>
            <b-td class="border-right vertical-align-top px-2">
              <div
                class="preset-column"
              >
                <b-button
                  v-for="(defaultPreset,index) in wrapper.defaultPresets"
                  :key="index"
                  variant="outline-secondary"
                  size="sm"
                  :class="getPresetButtomClass(defaultPreset)"
                  class="outline-light-secondary"
                  block
                  @click="setPreset(defaultPreset)"
                >
                  {{ defaultPreset.name }}
                </b-button>
                <hr>
                <div class="my-2 text-center">
                  {{ $tk('ClientPortal.MasterTable.CustomPresets') }}
                </div>
                <div
                  v-for="customPreset in wrapper.customPresets"
                  :key="customPreset.id"
                  class="d-flex justify-content-between mb-50"
                >
                  <b-button
                    :key="'button-'+customPreset.id"
                    variant="outline-secondary"
                    size="sm"
                    :class="getPresetButtomClass(customPreset)"
                    class="flex-fill outline-light-secondary"
                    @click="setPreset(customPreset)"
                  >
                    {{ customPreset.name }}
                  </b-button>
                  <b-button
                    :key="'delete-'+customPreset.id"
                    size="sm"
                    variant="link"
                    class="text-secondary align-self-center"
                    @click="deletePreset(customPreset)"
                  >
                    <GjIcon name="Delete" />
                  </b-button>
                </div>
                <b-button
                  v-show="showSaveSelectedPresetButton"
                  variant="outline-secondary"
                  size="sm"
                  class="my-2 active-preset outline-light-secondary"
                  @click="saveSelectedPreset"
                >
                  <GjIcon name="Save" />
                  {{ $tk('ClientPortal.MasterTable.SaveSelected') }}
                </b-button>
                <div id="savePresetContainer">
                  <b-button
                    v-show="showSaveAsNewPresetButton"
                    id="edit-preset-modal"
                    variant="outline-secondary"
                    size="sm"
                    class="my-2 active-preset outline-light-secondary"
                    block
                    @click="openSavePresetModal()"
                  >
                    <GjIcon
                      name="Save"
                      class="mr-50"
                    />
                    {{ $tk('ClientPortal.MasterTable.SaveAsNew') }}
                  </b-button>
                  <!--Popover for save preset-->
                  <b-popover
                    ref="savePresetModal"
                    target="edit-preset-modal"
                    container="savePresetContainer"
                    placement="bottom"
                  >
                    <b-form-group
                      :label="$tk('ClientPortal.MasterTable.NameOfPreset')"
                      label-size="xs"
                    >
                      <b-form-input
                        v-model="savePresetModal.value"
                        size="sm"
                        type="text"
                        min="1"
                        max="20"
                      />
                    </b-form-group>
                    <div class="text-right">
                      <b-button
                        class="m-0"
                        variant="outline-secondary"
                        size="sm"
                        @click="closeSavePresetModal"
                      >
                        {{ $tk('ClientPortal.MasterTable.Cancel') }}
                      </b-button>
                      <b-button
                        size="sm"
                        variant="primary"
                        class="m-0 ml-1"
                        @click="saveAsNewPreset"
                      >
                        {{ $tk('ClientPortal.MasterTable.Save') }}
                      </b-button>
                    </div>
                  </b-popover>
                </div>
              </div>
            </b-td>
            <b-td class="border-right vertical-align-top pl-2 pr-25">
              <div class="preset-column">
                <template v-for="column in wrapper.columns">
                  <b-form-checkbox
                    v-if="column.property != 'name'"
                    :key="column.id"
                    class="mb-1"
                    :checked="isColumnChecked(column.property)"
                    @change="columnChecked(column.property)"
                  >
                    {{ column.name }}
                  </b-form-checkbox>
                </template>
              </div>
            </b-td>
            <b-td class="vertical-align-top pl-2 pr-0">
              <draggable
                v-model="wrapper.config.preset.columns"
                class="list-group preset-column pr-2"
                v-bind="dragOptions"
                @start="drag = true"
                @end="drag = false"
              >
                <transition-group
                  type="transition"
                  :name="!drag ? 'flip-list' : null"
                >
                  <div
                    v-for="columnKey in wrapper.config.preset.columns"
                    :key="columnKey"
                    class="order-columns form-check form-check-flat form-check-primary mb-50 mt-0 list-group-item"
                  >
                    <label class="form-check-label ml-0 eclipsis">
                      <GjIcon
                        name="Drag"
                        class="mr-2"
                      />
                      {{ wrapper.columns[columnKey].name }}
                    </label>
                  </div>
                </transition-group>
              </draggable>
            </b-td>
          </b-tr>
        </b-tbody>
      </b-table-simple>
      <template #modal-footer>
        <b-button
          size="sm"
          class="m-0"
          variant="outline-secondary"
          @click="cancelPresetChanges"
        >
          {{ $tk('ClientPortal.MasterTable.Cancel') }}
        </b-button>
        <b-button
          size="sm"
          variant="primary"
          class="m-0 ml-1 float-right"
          @click="applyPresetChanges"
        >
          {{ $tk('ClientPortal.MasterTable.Apply') }}
        </b-button>
      </template>
    </b-modal>
    <!--Heatmap modal-->
    <b-modal
      id="ad-heatmap-modal"
      ref="heatmapModal"
      centered
      :cancel-title="$tk('ClientPortal.Form.Actions.Close')"
      cancel-variant="outline-secondary"
      :ok-disabled="true"
      modal-class="no-confirm-button"
      :title="$tk('ClientPortal.AdHeatmap')"
      :size="heatmapModel.width >= 800 ? 'xl' : 'lg'"
      @cancel="closeAdHeatmapModal"
    >
      <div class="catalog-items mb-0">
        <div class="text-center mb-0">
          <div
            v-if="heatmapModel.adSubtype == 'Image'"
            id="heatmap"
            style="display:inline-flex;"
            @mousemove="onHeatmapMouseMove($event)"
            @mouseleave="onHeatmapMouseOut()"
          >
            <img
              :src="heatmapModel.mediaUrl"
              :style="{ 'width': heatmapModel.width }"
              @load="generateHeatmap(heatmapModel.height,heatmapModel.width)"
            >
            <div class="b-tooltip heatmap-tooltip" />
          </div>
          <div
            v-else-if="heatmapModel.adSubtype == 'External'"
            id="heatmap"
            style="display:inline-flex;"
            @mousemove="onHeatmapMouseMove($event)"
            @mouseleave="onHeatmapMouseOut()"
          >
            <div
              class="content ml-0"
              @load="generateHeatmap(heatmapModel.height,heatmapModel.width)"
              v-html="decodeURIComponent(heatmapModel.mediaUrl)"
            />
            <div class="b-tooltip heatmap-tooltip" />
          </div>
          <div
            v-else
            id="heatmap"
            class="d-inline-flex"
            @mousemove="onHeatmapMouseMove($event)"
            @mouseleave="onHeatmapMouseOut()"
          >
            <iframe
              :src="heatmapModel.mediaUrl"
              :height="heatmapModel.height"
              :width="heatmapModel.width"
              frameborder="0"
              scrolling="auto"
              @load="generateHeatmap(heatmapModel.height,heatmapModel.width)"
            />
            <div class="b-tooltip heatmap-tooltip" />
          </div>
        </div>
      </div>
    </b-modal>
  </section>
</template>

<script>

import GjIcon from '@gjirafatech/gjirafa-icons/Icon.vue'
import notifications from '@/mixins/toastification'
import MasterTableRow from '@/views/client/ads-manager/statistics/MasterTableRow.vue'
import draggable from 'vuedraggable'
import StatisticsService from '@/api/client/statistics.service'
import AdGroupsService from '@/api/client/adgroups.service'
import CampaignsService from '@/api/client/campaigns.service'
import AdsService from '@/api/client/ads.service'
import * as h337 from 'heatmap.js'
import { AccountAccessManager } from '@/utils/accessManager/accessManager'
import loader from '@/components/Loader.vue'
import MasterTableHeader from './MasterTableHeader.vue'
import MasterTableSkeleton from './MasterTableSkeleton.vue'
import MasterTableHeaderSkeleton from './MasterTableHeaderSkeleton.vue'
import MultiselectTags from '../create/MultiselectTags.vue'

const ENTITY_TYPES = {
  CAMPAIGN: 1,
  AD_GROUP: 2,
  AD: 3,
  BREAKDOWN: 4,
  SUMMARY: 5,
}

const SORT = {
  ASCENDING: 0,
  DESCENDING: 1,
}

const AD_STATUSES = {
  ARCHIVE: 11,
  DELETE: 12,
}

const TABS = {
  CAMPAIGNS: 'campaigns',
  AD_GROUPS: 'adgroups',
  ADS: 'ads',
}

export default {
  components: {
    GjIcon,
    MasterTableRow,
    draggable,
    MasterTableHeader,
    MasterTableSkeleton,
    MasterTableHeaderSkeleton,
    loader,
    MultiselectTags,
  },
  mixins: [notifications],
  props: {
    wrapper: {
      type: Object,
      default: null,
    },
    stats: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    loadingStatistics: {
      type: Boolean,
      default: false,
    },
    campaignCurrentPage: {
      type: Number,
      default: 1,
    },
    adGroupCurrentPage: {
      type: Number,
      default: 1,
    },
    adCurrentPage: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      loadingData: this.loading,
      titleWidthCampaign: 300,
      titleWidthAdgroup: 300,
      titleWidthAd: 300,
      perPage: 40,
      drag: false,
      loadingChartStats: true,
      savingConfig: false,
      copyCampaignModel: {
        campaignId: 0,
        name: 'data',
        numberOfCopies: 1,
        pauseNewAdGroups: true,
        includeAdGroups: true,
        copyAdGroupsTargeting: true,
        includeAds: true,
      },
      copyAdGroupModel: {
        adGroupId: 0,
        numberOfCopies: 1,
        pauseNewAdGroups: true,
        campaignId: 0,
        campaignIds: [],
        selectedCampaigns: [],
        adGroups: true,
        ads: true,
      },
      copyAdModel: {
        adId: 0,
        numberOfCopies: 1,
        campaignId: 0,
        adGroupId: 0,
        adGroupIds: [],
        selectedAdGroups: [],
        pauseNewAds: true,
      },
      campaignList: [],
      adGroupList: [],
      presetBck: {},
      modalOpened: false,
      hasPresetChanges: false,
      savePresetModal: {
        top: 0,
        left: 0,
        show: false,
        value: '',
      },
      saveFilterModal: {
        name: '',
        loading: false,
      },
      confirmDialogType: {
        tabType: 1,
        actionType: 1,
        loading: false,
      },
      campaignEditModel: null,
      adGroupEditModel: null,
      adEditModel: null,

      selectedPropertyFilters: [],
      selectedPropertyFiltersWrapper: {},

      heatmapModel: {
        adName: '',
        width: 0,
        height: 0,
        mediaUrl: '',
        adSubtype: '',
        coordinates: [],
      },
      heatmapInstance: {

      },
      accountAccessManager: AccountAccessManager,
      duplicating: false,
    }
  },
  computed: {
    dragOptions() {
      return {
        animation: 200,
        disabled: false,
        ghostClass: 'ghost',
      }
    },
    breakdownText: {
      get() {
        if (this.wrapper.config.filter.breakdown) return `Breakdown (${this.wrapper.config.filter.breakdown})`
        return 'Breakdown'
      },
      set(breakdown) {
        return breakdown
      },
    },
    showSaveSelectedPresetButton() {
      return this.hasPresetChanges && !this.wrapper.config.preset.isDefault
    },
    showSaveAsNewPresetButton() {
      return this.hasPresetChanges
    },
    campaignsSummary() {
      return this.calculateSummary(this.stats.campaignTabStatistics.entities, this.wrapper.config.filter.campaignIds)
    },
    adGroupsSummary() {
      return this.calculateSummary(this.stats.adGroupTabStatistics.entities, this.wrapper.config.filter.adGroupIds)
    },
    adsSummary() {
      return this.calculateSummary(this.stats.adTabStatistics.entities, this.wrapper.config.filter.adIds)
    },
    tabEnum() {
      return TABS
    },
    createAdGroupUrl() {
      if (this.wrapper.config.filter.campaignIds.length === 1) {
        return `/${this.$route.params.accountId}/adgroup/create?campaignId=${this.wrapper.config.filter.campaignIds[0]}`
      }
      return `/${this.$route.params.accountId}/adgroup/create`
    },
    createAdUrl() {
      if (this.wrapper.config.filter.adGroupIds.length === 1) {
        return `/${this.$route.params.accountId}/ad/create?adGroupId=${this.wrapper.config.filter.adGroupIds[0]}`
      }
      return `/${this.$route.params.accountId}/ad/create`
    },
  },
  methods: {
    changePage(value) {
      this.$emit('changePage', value)
    },
    resizeCampaign() {
      this.titleWidthCampaign = this.$refs.campaign != null ? this.$refs.campaign.clientWidth + 1 : 300
    },
    resizeAdgroup() {
      this.titleWidthAdgroup = this.$refs.adgroup != null ? this.$refs.adgroup.clientWidth + 1 : 300
    },
    resizeAd() {
      this.titleWidthAd = this.$refs.ad != null ? this.$refs.ad.clientWidth + 1 : 300
    },
    getColumnSortedClass(column) {
      return this.wrapper.config.filter.sortColumn === column ? 'active' : ''
    },
    addCustomClass(column) {
      var isColumnSortedCssClass = this.getColumnSortedClass(column)
      if (this.wrapper.columns[column].inputType === 4) {
        return `table-cell-text ${isColumnSortedCssClass}`
      } if (this.wrapper.columns[column].inputType === 3) {
        return `table-cell-status ${isColumnSortedCssClass}`
      }
      return isColumnSortedCssClass
    },
    copyEntity(entity) {
      if (entity.entityType === ENTITY_TYPES.CAMPAIGN) {
        this.openCampaignCopyModal(entity)
      } else if (entity.entityType === ENTITY_TYPES.AD_GROUP) {
        this.openAdGroupCopyModal(entity)
      } else if (entity.entityType === ENTITY_TYPES.AD) {
        this.openAdCopyModal(entity)
      }
    },
    openCampaignCopyModal(campaign) {
      this.$refs.copyCampaignModal.show()
      this.copyCampaignModel = {
        campaignId: 0,
        name: 'data',
        numberOfCopies: 1,
        pauseNewAdGroups: true,
        includeAdGroups: true,
        copyAdGroupsTargeting: true,
        includeAds: true,
      }
      this.copyCampaignModel.campaignId = campaign.id
      this.copyCampaignModel.name = `${campaign.name} copy`
    },
    openAdGroupCopyModal(adGroup) {
      this.$refs.copyAdgroupModal.show()
      this.copyAdGroupModel = {
        adGroupId: 0,
        numberOfCopies: 1,
        pauseNewAdGroups: true,
        campaignId: 0,
        campaignIds: [],
        selectedCampaigns: [],
        adGroups: true,
        ads: true,
      }
      this.copyAdGroupModel.adGroupId = adGroup.id
      this.copyAdGroupModel.selectedCampaigns = []
      this.getAccountCampaigns(adGroup)
    },
    openAdCopyModal(ad) {
      this.$refs.copyAdCopyModal.show()
      this.copyAdModel = {
        adId: 0,
        numberOfCopies: 1,
        campaignId: 0,
        adGroupId: 0,
        adGroupIds: [],
        selectedAdGroups: [],
        pauseNewAds: true,
      }
      this.copyAdModel.adId = ad.id
      this.adCopyAdGroups(ad.id)
    },
    async getAccountCampaigns(adGroup) {
      try {
        const response = await AdGroupsService.getAdGroupCampaign(adGroup, this.$route.params.accountId)
        if (response) {
          this.campaignList = response.campaigns
          this.copyAdGroupModel.selectedCampaigns.push({ id: response.campaignModel.id, name: response.campaignModel.name })
        }
      } catch (error) {
        this.error(error.message)
      }
    },
    async adCopyAdGroups(id) {
      try {
        const response = await CampaignsService.getCampaigns(id, this.$route.params.accountId)
        this.adGroupList = response
      } catch (error) {
        this.error(error.message)
      }
    },
    async saveCopiedCampaign(campaign) {
      this.duplicating = true
      try {
        const response = await CampaignsService.duplicateCampaign(campaign, this.$route.params.accountId)
        if (response.copied) {
          this.$refs.copyCampaignModal.hide()
          this.success(this.$tk('ClientPortal.MasterTable.CampaignDuplicatedSuccessfully'))
          this.reloadStatistics()
        } else {
          this.error(response.message)
        }
      } catch (error) {
        this.error(error.message)
      }
      this.duplicating = false
    },
    async saveCopiedAdGroup(adGroup) {
      this.duplicating = true
      adGroup.campaignIds = []
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < adGroup.selectedCampaigns.length; i++) {
        const campaign = adGroup.selectedCampaigns[i]
        adGroup.campaignIds.push(campaign.id)
      }

      try {
        const response = await AdGroupsService.duplicateAdGroup(adGroup, this.$route.params.accountId)
        if (response.copied) {
          this.$refs.copyAdgroupModal.hide()
          this.success(response.message)
          this.reloadStatistics()
        } else {
          this.$set(adGroup, 'error', response.message)
          adGroup.numberOfCopies = 0
        }
      } catch (error) {
        this.error(error.message)
      }
      this.duplicating = false
    },
    isDuplicateAdModalCompleted(entity) {
      if (entity === 1) {
        return this.copyCampaignModel.numberOfCopies && this.copyCampaignModel.name
      } if (entity === 2) {
        return this.copyAdGroupModel.numberOfCopies && this.copyAdGroupModel.selectedCampaigns.length
      } return this.copyAdModel.numberOfCopies && this.copyAdModel.selectedAdGroups.length
    },
    async saveCopiedAd(ad) {
      this.duplicating = true
      ad.adGroupIds = []
      // eslint-disable-next-line no-plusplus
      for (let adGroup = 0; adGroup < ad.selectedAdGroups.length; adGroup++) {
        ad.adGroupIds.push(ad.selectedAdGroups[adGroup].id)
      }

      try {
        const response = await AdsService.duplicateAd(ad, this.$route.params.accountId)
        if (response.copied) {
          this.$refs.copyAdCopyModal.hide()
          this.success(response.message)
          this.reloadStatistics()
        } else {
          this.error(response.message)
        }
      } catch (error) {
        this.error(error.message)
      }
      this.copyAdModel.selectedAdGroups.splice(0, this.copyAdModel.selectedAdGroups.length)
      this.duplicating = false
    },
    getSortOrder() {
      if (this.wrapper.config.filter.sortDirection === SORT.ASCENDING) return 'ArrowUp'
      return 'ArrowDown'
    },
    setSortDirection(column) {
      this.wrapper.config.filter.sortColumn = column
      if (this.wrapper.config.filter.sortDirection === SORT.ASCENDING) {
        this.wrapper.config.filter.sortDirection = SORT.DESCENDING
      } else {
        this.wrapper.config.filter.sortDirection = SORT.ASCENDING
      }
      this.reloadStatistics()
      this.saveConfig(true)
    },
    editEntity(entity) {
      if (entity.entityType === ENTITY_TYPES.CAMPAIGN) {
        this.openCampaignEditModal(entity)
      } else if (entity.entityType === ENTITY_TYPES.AD_GROUP) {
        this.openAdGroupEditModal(entity)
      } else if (entity.entityType === ENTITY_TYPES.AD) {
        this.openAdEditModal(entity)
      }
    },
    // heatmap
    async openHeatmapModal(entity) {
      this.$refs.heatmapModal.show()
      this.heatmapModel.mediaUrl = ''

      try {
        const model = {
          adId: entity.id,
          dateRange: this.wrapper.config.filter.dateRange,
        }
        const response = await AdsService.getHeatmap(model, this.$route.params.accountId)
        this.heatmapModel.adName = response.adName
        this.heatmapModel.mediaUrl = response.mediaUrl
        this.heatmapModel.width = response.width
        this.heatmapModel.height = response.height
        this.heatmapModel.adSubtype = entity.entityAdSubtype
        this.heatmapModel.coordinates = []
        // eslint-disable-next-line no-plusplus
        for (let i = 0; i < response.heatmap.length; i++) {
          const coordinate = response.heatmap[i]
          this.heatmapModel.coordinates.push({ x: coordinate.screenX, y: coordinate.screenY, value: coordinate.count })
        }
      } catch (error) {
        this.error(error.message)
      }
    },
    onHeatmapMouseMove(ev) {
      const tooltip = document.querySelector('.b-tooltip')
      const x = ev.layerX
      const y = ev.layerY
      const value = this.heatmapInstance.getValueAt({
        x,
        y,
      })
      tooltip.style.display = 'block'
      const transl = `translate(${x + 10}px, ${y + 10}px)`
      tooltip.style.webkitTransform = transl
      tooltip.innerHTML = value
    },
    onHeatmapMouseOut() {
      var tooltip = document.querySelector('.b-tooltip')
      tooltip.style.display = 'none'
    },
    adjustHeight(contentHeight, correctWidth) {
      const correctHeight = contentHeight - 15
      const heatmapCanvas = Array.from(document.querySelectorAll('.heatmap-canvas')).pop()
      heatmapCanvas.style.height = `${correctHeight}px`
      heatmapCanvas.style.width = `${correctWidth}px`
    },
    generateHeatmap(height, width) {
      this.heatmapInstance = h337.create({
        container: document.getElementById('heatmap'),
        radius: 15,
        maxOpacity: 0.9,
        minOpacity: 0.3,
      })

      const points = []
      let max = 0
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < this.heatmapModel.coordinates.length; i++) {
        max = Math.max(max, this.heatmapModel.coordinates[i].value)
        const point = {
          x: this.heatmapModel.coordinates[i].x,
          y: this.heatmapModel.coordinates[i].y,
          value: this.heatmapModel.coordinates[i].value,
        }
        points.push(point)
      }
      const data = {
        max,
        data: points,
      }
      this.heatmapInstance.setData(data)
      this.adjustHeight(height, width)
    },
    closeAdHeatmapModal() {
      this.$refs.heatmapModal.hide()
    },
    // Statistics methods
    reloadStatistics() {
      this.$emit('reload')
    },
    loadStatistics() {
      this.$emit('loadStats')
    },
    saveConfig(pushState = false) {
      this.$emit('config', pushState)
    },
    setBreakdown(breakdown) {
      if (this.wrapper.config.filter.breakdown === breakdown.value) {
        this.wrapper.config.filter.breakdown = null
        this.breakdownText = 'Breakdown'
      } else {
        this.wrapper.config.filter.breakdown = breakdown.value
        this.breakdownText = `Breakdown (${breakdown.name})`
      }
      this.onFiltersChanged(true)
    },
    onFiltersChanged() {
      this.saveConfig(true)
      this.reloadStatistics()
    },
    // preset
    openPresetModal() {
      this.presetBck = JSON.parse(JSON.stringify(this.wrapper.config.preset))
      this.modalOpened = true
      this.drag = true
      this.$refs.presetModal.show()
    },
    getPresetButtomClass(preset) {
      if (this.wrapper.config.preset.id === preset.id) return 'bg-primary-light active-preset'
      return ''
    },
    applyPresetChanges() {
      this.modalOpened = false
      this.presetBck = {}
      this.drag = false
      this.hasPresetChanges = true
      this.savePresetModal.show = false
      this.$refs.presetModal.hide()
    },
    isColumnChecked(column) {
      return this.wrapper.config.preset.columns.indexOf(column) >= 0
    },
    columnChecked(column) {
      const { columns } = this.wrapper.config.preset
      const index = columns.indexOf(column)
      if (index >= 0) {
        columns.splice(index, 1)
      } else {
        columns.push(column)
      }
      this.hasPresetChanges = true
      this.savePresetModal.show = false
    },
    openSavePresetModal() {
      this.savePresetModal.show = true
      this.savePresetModal.value = `Preset #${this.wrapper.customPresets.length + 1}`
    },
    closeSavePresetModal() {
      this.$root.$emit('bv::hide::popover', 'edit-preset-modal')
      this.savePresetModal.left = 0
      this.savePresetModal.top = 0
      this.savePresetModal.value = ''
      this.savePresetModal.show = false
    },
    setPreset(preset) {
      this.wrapper.config.preset = preset
    },
    async saveAsNewPreset() {
      if (!this.savePresetModal.value) return
      const preset = JSON.parse(JSON.stringify(this.wrapper.config.preset))
      preset.name = this.savePresetModal.value

      try {
        const response = await StatisticsService.createPreset(preset, this.$route.params.accountId)
        if (response.inserted) {
          preset.id = response.id
          this.wrapper.config.preset = preset
          this.wrapper.customPresets.push(preset)
          this.wrapper.defaultPresets.push(preset)
          this.closeSavePresetModal()
          this.hasPresetChanges = false
        }
      } catch (error) {
        this.error(error.message)
      }
    },
    async saveSelectedPreset() {
      const { preset } = this.wrapper.config

      try {
        const response = await StatisticsService.updatePreset(preset, this.$route.params.accountId)
        if (response.updated) {
          this.success(this.$tk('ClientPortal.MasterTable.PresetUpdatedSuccessfully'))
        }
      } catch (error) {
        this.error(error.message)
      }
    },
    async deletePreset(preset) {
      const index = this.wrapper.customPresets.indexOf(preset)
      const defaultPresetIndex = this.wrapper.defaultPresets.findIndex(prs => prs.id === preset.id)
      try {
        const response = await StatisticsService.deletePreset(preset, this.$route.params.accountId)
        if (response.deleted) {
          this.wrapper.customPresets.splice(index, 1)
          this.wrapper.defaultPresets.splice(defaultPresetIndex, 1)
          // eslint-disable-next-line prefer-destructuring
          this.wrapper.config.preset = this.wrapper.defaultPresets[0]
        }
      } catch (error) {
        this.error(error.message)
      }
    },
    addSelectedCampaign(selectedCampaign) {
      this.copyAdGroupModel.selectedCampaigns.push(selectedCampaign)
    },
    removeSelectedCampaign(selectedCampaign) {
      const index = this.copyAdGroupModel.selectedCampaigns.findIndex(x => x.id === selectedCampaign.id)
      this.copyAdGroupModel.selectedCampaigns.splice(index, 1)
    },
    addSelectedAdGroup(selectedAdGroup) {
      this.copyAdModel.selectedAdGroups.push(selectedAdGroup)
    },
    removeSelectedAdGroup(selectedAdGroup) {
      const index = this.copyAdModel.selectedAdGroups.findIndex(x => x.id === selectedAdGroup.id)
      this.copyAdModel.selectedAdGroups.splice(index, 1)
    },
    onTabChange(tab) {
      if (tab === this.wrapper.tab) {
        return
      }

      const documentLocation = document.location
      const url = new URL(documentLocation)
      url.searchParams.set('tab', tab)
      this.pushState(url.toString())
      this.setTab(tab)
    },
    cancelPresetChanges() {
      if (this.modalOpened) {
        this.wrapper.config.preset = this.presetBck
        this.presetBck = {}
        this.modalOpened = false
      }
      this.$refs.presetModal.hide()
    },
    setTab(tab) {
      this.$emit('tab', tab)
      this.loadStatistics()
    },
    pushState(url) {
      this.$emit('push', url)
    },
    syncState() {
      this.$emit('sync')
    },
    selectAllCampaigns(selected) {
      this.wrapper.config.filter.campaignIds.splice(0, this.wrapper.config.filter.campaignIds.length)
      this.wrapper.config.filter.adGroupIds.splice(0, this.wrapper.config.filter.adGroupIds.length)
      this.wrapper.config.filter.adIds.splice(0, this.wrapper.config.filter.adIds.length)
      this.stats.campaignTabStatistics.entities.forEach(entity => {
        entity.selected = selected
        if (selected) {
          this.wrapper.config.filter.campaignIds.push(entity.id)
        }
      })
      this.stats.adGroupTabStatistics.needsReload = true
      this.stats.adTabStatistics.needsReload = true
      this.stats.campaignTabStatistics.selectedAll = selected
      this.saveConfig(true)
      this.reloadChart()
    },
    selectAllAdGroups(selected) {
      this.wrapper.config.filter.adGroupIds.splice(0, this.wrapper.config.filter.adGroupIds.length)
      this.wrapper.config.filter.adIds.splice(0, this.wrapper.config.filter.adIds.length)
      this.stats.adGroupTabStatistics.entities.forEach(entity => {
        entity.selected = selected
        if (selected) {
          this.wrapper.config.filter.adGroupIds.push(entity.id)
        }
      })
      this.stats.adTabStatistics.needsReload = true
      this.stats.adGroupTabStatistics.selectedAll = selected
      this.saveConfig(true)
      this.reloadChart()
    },
    selectAllAds(selected) {
      this.wrapper.config.filter.adIds.splice(0, this.wrapper.config.filter.adIds.length)
      this.stats.adTabStatistics.entities.forEach(entity => {
        entity.selected = selected
        if (selected) {
          this.wrapper.config.filter.adIds.push(entity.id)
        }
      })
      this.stats.adTabStatistics.selectedAll = selected
      this.saveConfig(true)
      this.reloadChart()
    },
    reloadChart() {
      this.$emit('chart')
    },
    onEntityClick(entity) {
      if (entity.entityType === ENTITY_TYPES.CAMPAIGN) {
        this.onCampaignClick(entity)
      } else if (entity.entityType === ENTITY_TYPES.AD_GROUP) {
        this.onAdGroupClick(entity)
      } else if (entity.entityType === ENTITY_TYPES.AD) {
        this.onAdClick(entity)
      }
    },
    onCampaignClick(entity) {
      this.onCampaignSelect(entity)
      this.setTab(TABS.AD_GROUPS)
    },
    onAdGroupClick(entity) {
      this.onAdGroupSelect(entity)
      this.setTab(TABS.ADS)
    },
    onAdClick(entity) {
      this.onAdSelect(entity)
    },
    onEntitySelect(entity) {
      if (entity.entityType === ENTITY_TYPES.CAMPAIGN) {
        if (!entity.selected && this.stats.campaignTabStatistics.selectedAll) {
          this.stats.campaignTabStatistics.selectedAll = false
        }
        this.onCampaignSelect(entity)
      } else if (entity.entityType === ENTITY_TYPES.AD_GROUP) {
        if (!entity.selected && this.stats.adGroupTabStatistics.selectedAll) {
          this.stats.adGroupTabStatistics.selectedAll = false
        }
        this.onAdGroupSelect(entity)
      } else if (entity.entityType === ENTITY_TYPES.AD) {
        if (!entity.selected && this.stats.adTabStatistics.selectedAll) {
          this.stats.adTabStatistics.selectedAll = false
        }
        this.onAdSelect(entity)
      }
    },
    onCampaignSelect(entity) {
      const { campaignIds } = this.wrapper.config.filter
      const index = campaignIds.indexOf(entity.id)
      if (entity.selected && index < 0) {
        campaignIds.push(entity.id)
      } else if (!entity.selected && index >= 0) {
        campaignIds.splice(index, 1)
      }
      this.wrapper.config.filter.adGroupPage = 0
      this.wrapper.config.filter.adGroupIds.splice(0, this.wrapper.config.filter.adGroupIds.length)
      this.wrapper.config.filter.adPage = 0
      this.wrapper.config.filter.adIds.splice(0, this.wrapper.config.filter.adIds.length)
      this.stats.adGroupTabStatistics.needsReload = true
      this.stats.adTabStatistics.needsReload = true
      this.saveConfig(true)
      this.reloadChart()
    },
    onAdGroupSelect(entity) {
      const { adGroupIds } = this.wrapper.config.filter
      const index = adGroupIds.indexOf(entity.id)
      if (entity.selected && index < 0) {
        adGroupIds.push(entity.id)
      } else if (!entity.selected && index >= 0) {
        adGroupIds.splice(index, 1)
      }
      this.wrapper.config.filter.adPage = 0
      this.wrapper.config.filter.adIds.splice(0, this.wrapper.config.filter.adIds.length)
      this.stats.adTabStatistics.needsReload = true
      this.saveConfig(true)
      this.reloadChart()
    },
    onAdSelect(entity) {
      const { adIds } = this.wrapper.config.filter
      const index = adIds.indexOf(entity.id)
      if (entity.selected && index < 0) {
        adIds.push(entity.id)
      } else if (!entity.selected && index >= 0) {
        adIds.splice(index, 1)
      }
      this.saveConfig(true)
      this.reloadChart()
    },
    // Bulk operations
    archiveCampaigns() {
      this.confirmDialogType.tabType = TABS.CAMPAIGNS
      this.confirmDialogType.actionType = AD_STATUSES.ARCHIVE
      this.$refs.confirmationModal.show()
    },
    deleteCampaigns() {
      this.confirmDialogType.tabType = TABS.CAMPAIGNS
      this.confirmDialogType.actionType = AD_STATUSES.DELETE
      this.$refs.confirmationModal.show()
    },
    archiveAdGroups() {
      this.confirmDialogType.tabType = TABS.AD_GROUPS
      this.confirmDialogType.actionType = AD_STATUSES.ARCHIVE
      this.$refs.confirmationModal.show()
    },
    deleteAdGroups() {
      this.confirmDialogType.tabType = TABS.AD_GROUPS
      this.confirmDialogType.actionType = AD_STATUSES.DELETE
      this.$refs.confirmationModal.show()
    },
    archiveAds() {
      this.confirmDialogType.tabType = TABS.ADS
      this.confirmDialogType.actionType = AD_STATUSES.ARCHIVE
      this.$refs.confirmationModal.show()
    },
    deleteAds() {
      this.confirmDialogType.tabType = TABS.ADS
      this.confirmDialogType.actionType = AD_STATUSES.DELETE
      this.$refs.confirmationModal.show()
    },
    confirmDialogYes() {
      const status = this.confirmDialogType.actionType
      const type = this.confirmDialogType.tabType
      this.confirmDialogType.loading = true

      if (type === TABS.CAMPAIGNS) {
        this.changeCampaignStatus(status)
      } else if (type === TABS.AD_GROUPS) {
        this.changeAdGroupStatus(status)
      } else if (type === TABS.ADS) {
        this.changeAdStatus(status)
      }
      this.$refs.confirmationModal.hide()
      this.confirmDialogType.loading = false
    },
    async changeCampaignStatus(status) {
      this.loadingData = true
      try {
        const response = await StatisticsService.updateStatus(1, status, this.wrapper.config.filter.campaignIds, this.$route.params.accountId)
        if (response.saved) {
          this.stats.campaignTabStatistics.needsReload = true
          this.stats.adGroupTabStatistics.needsReload = true
          this.stats.adTabStatistics.needsReload = true
          this.wrapper.config.filter.campaignIds = []
          this.stats.adGroupTabStatistics.entities = []
          this.stats.adTabStatistics.entities = []
          this.success(response.message)
          this.loadStatistics()
        }
      } catch (error) {
        this.error(error.message)
      }
    },
    async changeAdGroupStatus(status) {
      this.loadingData = true
      try {
        const response = await StatisticsService.updateStatus(2, status, this.wrapper.config.filter.adGroupIds, this.$route.params.accountId)
        if (response.saved) {
          this.stats.adGroupTabStatistics.needsReload = true
          this.stats.adTabStatistics.needsReload = true
          this.wrapper.config.filter.adGroupIds = []
          this.stats.adTabStatistics.entities = []
          this.success(response.message)
          this.loadStatistics()
        }
      } catch (error) {
        this.error(error.message)
      }
    },
    async changeAdStatus(status) {
      this.loadingData = true
      try {
        const response = await StatisticsService.updateStatus(3, status, this.wrapper.config.filter.adIds, this.$route.params.accountId)
        if (response.saved) {
          this.stats.adTabStatistics.needsReload = true
          this.wrapper.config.filter.adIds = []
          this.success(response.message)
          this.loadStatistics()
        }
      } catch (error) {
        this.error(error.message)
      }
    },

    calculateSummary(entities, selectedIds) {
      let views = 0
      let uniqueViews = 0
      let clicks = 0
      let uniqueClicks = 0
      let impressions = 0
      let uniqueImpressions = 0
      let actions = 0
      let uniqueActions = 0
      let videoViews = 0
      let videoImpressions = 0
      let viewsCost = 0
      let impressionsCost = 0
      let clicksCost = 0
      let actionsCost = 0
      let dailyBudget = 0
      let totalBudget = 0

      entities.forEach(entity => {
        if (selectedIds.length > 0 && selectedIds.indexOf(entity.id) < 0) {
          return
        }

        views += entity.views
        clicks += entity.clicks
        actions += entity.actions
        impressions += entity.impressions

        uniqueViews += entity.uniqueViews
        uniqueClicks += entity.uniqueClicks
        uniqueActions += entity.uniqueActions
        uniqueImpressions += entity.uniqueImpressions

        if (entity.actions > 0) {
          videoViews += entity.views
          videoImpressions += entity.impressions
        }

        viewsCost += entity.viewsCost
        clicksCost += entity.clicksCost
        actionsCost += entity.actionsCost
        impressionsCost += entity.impressionCost

        dailyBudget += parseFloat(entity.dailyBudget)
        totalBudget += parseFloat(entity.totalBudget)
      })

      const atr = videoViews > 0 ? (actions / videoViews) * 100 : 0
      const iAtr = videoViews > 0 ? (actions / videoImpressions) * 100 : 0
      const ctr = (views > 0 && clicks > 0) ? (clicks / views) * 100 : 0
      const iCtr = (impressions > 0 && clicks > 0) ? (clicks / impressions) * 100 : 0
      const averageCpa = actions > 0 ? actionsCost / actions : 0
      const averageCpm = views > 0 ? (viewsCost * 1000) / views : 0
      const averageCpc = clicks > 0 ? clicksCost / clicks : 0

      const summary = {
        name: 'Summary',
        entityType: ENTITY_TYPES.SUMMARY,
        totalBudget,
        dailyBudget,
        startDate: '-',
        endDate: '-',
        status: '-',
        views,
        uniqueViews,
        clicks,
        uniqueClicks,
        actions,
        uniqueActions,
        impressions,
        uniqueImpressions,
        ctr,
        atr,
        iAtr,
        iCtr,
        cost: (viewsCost + clicksCost + actionsCost + impressionsCost),
        frequency: 0,
        avgCpa: averageCpa,
        avgCpm: averageCpm,
        avgCpc: averageCpc,
      }
      return summary
    },
  },
}
</script>

<style lang="scss">
@import '@/assets/scss/views/client/ads-manager/statistics/master-table.scss';
</style>
